import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Pressable, ScrollView, View } from 'react-native'
import Typography from '@ca/ui/typography'
import Select from '@ca/ui/forms/Select'
import Input from '@ca/ui/forms/Input'
import Checkbox, { type CheckboxProps } from '@ca/ui/forms/Checkbox'
import InputAction from '@ca/ui/forms/Input/InputAction'
import Button from '@ca/ui/buttons/Button'
import ModalWide from '@ca/ui/components/Modal/variants/ModalWide'
import Popover from '@ca/ui/components/Popover'
import Icon from '@ca/ui/elements/Icon'
import Glass from '@ca/ui/elements/Glass'
import CollapsibleSection from '@ca/ui/elements/CollapsibleSection'
import type {
  OpticalMeasureFreezeStatus,
  OpticalMeasureSettings,
  OpticalMeasureSettingsKeys,
} from '../index'
import { useFreezeStatus } from './faceDataStore'
import { z } from 'zod'

const numberValue = z.number().optional().catch(undefined)
const stringValue = z.string().optional().catch(undefined)
import {
  getSetting,
  setSetting,
  SETTINGS_CONFIG,
  type SettingInputConfig,
  type SectionConfig,
  type SettingLevel,
} from './settings'
import { useReanimatedKeyboardAnimation } from 'react-native-keyboard-controller'
import Animated, { useAnimatedStyle } from 'react-native-reanimated'

const FRAME_FREEZE_SECTION_ID = 'frameFreeze'

export type SettingsUtilitySectionId = typeof FRAME_FREEZE_SECTION_ID

type Props = {
  open: boolean
  close: () => void
  settings: OpticalMeasureSettings
  paths: readonly string[]
  sections?: readonly SettingsUtilitySectionId[]
  isDev?: boolean
  isDebug?: boolean
  onSave: (settings: OpticalMeasureSettings) => void
  onFreezeCurrentFrame?: () => void
  onSetLatestFreeze?: () => void
  onClearFreeze?: () => void
}

type SettingInfoProps = {
  text: string
  inputAction?: boolean
}

type SettingSelectProps = {
  label: string
  info: string
  value: string | undefined
  options: readonly { value: string; label: string }[]
  onChange: (value: string) => void
}

type NumInputProps = {
  label: string
  value: number | undefined
  integer?: boolean
  info?: string
  onChange: (v: number) => void
}

type SettingsSectionProps = {
  title: string
  children: ReactNode
  collapsible?: boolean
  open?: boolean
  onToggle?: () => void
}

const isSettingVisible = (
  level: SettingLevel = 'dev',
  isDev = false,
  isDebug = false
) => {
  if (level === 'debug') return isDebug
  return isDev
}

const getFreezeStatusLabel = (
  status: OpticalMeasureFreezeStatus | undefined
) => {
  if (!status?.active) return 'Live'
  if (status.processing) return 'Processing Frozen Frame'
  const source =
    status.source === 'latest-good' ? 'Latest Good Frame' : 'Manual Frame'
  const mode = status.mode ? `${status.mode.toUpperCase()} ` : ''
  return `Frozen ${mode}${source}`
}

function SettingsSection({
  title,
  children,
  collapsible,
  open = true,
  onToggle,
}: SettingsSectionProps) {
  const [hasOpened, setHasOpened] = useState(open)
  useEffect(() => {
    if (open) setHasOpened(true)
  }, [open])

  if (collapsible) {
    return (
      <CollapsibleSection
        open={open}
        onToggle={onToggle}
        contentClassName="px-4"
        header={
          <Typography variant="XS" className="text-black/60">
            {title}
          </Typography>
        }
      >
        {(open || hasOpened) && children}
      </CollapsibleSection>
    )
  }

  return (
    <View className="overflow-hidden rounded-md border border-black/20">
      <Typography variant="XS" className="mb-3 text-black/30">
        {title}
      </Typography>
      <View className="gap-3 p-4">{children}</View>
    </View>
  )
}

function SettingInfo({ text, inputAction }: SettingInfoProps) {
  const [open, setOpen] = useState(false)

  const toggle = () => setOpen((prev) => !prev)

  const trigger = inputAction ? (
    <InputAction icon="info-circle" color="rgba(0,0,0,0.35)" onPress={toggle} />
  ) : (
    <Pressable
      hitSlop={8}
      className="h-7 w-7 items-center justify-center"
      onPress={toggle}
    >
      <Icon name="info-circle" color="rgba(0,0,0,0.35)" />
    </Pressable>
  )

  return (
    <Popover
      open={open}
      onClose={() => setOpen(false)}
      position={['bottom', 'right']}
      offset={[0, 6]}
      customWidth={320}
      content={
        <Glass
          blur={50}
          className="rounded-xl border border-black/10 bg-white/80 p-4"
        >
          <Typography variant="XS" className="text-black/60">
            {text}
          </Typography>
        </Glass>
      }
    >
      {trigger}
    </Popover>
  )
}

function SettingCheckbox({ info, ...props }: CheckboxProps & { info: string }) {
  return (
    <View className="w-full flex-1 flex-row items-center justify-between gap-1">
      <Checkbox {...props} />
      <SettingInfo text={info} />
    </View>
  )
}

function SettingSelect({
  label,
  info,
  value,
  options,
  onChange,
}: SettingSelectProps) {
  return (
    <View className="relative">
      <Select
        label={label}
        labelVariant="inline"
        hideIndicator
        options={options}
        value={value ?? options[0]?.value ?? ''}
        onChange={onChange}
      />
      <View className="absolute right-10 top-0 h-12 justify-center">
        <SettingInfo text={info} />
      </View>
    </View>
  )
}

function NumInput({
  label,
  value,
  integer = false,
  info,
  onChange,
}: NumInputProps) {
  return (
    <Input
      label={label}
      labelVariant="inline"
      hideIndicator
      keyboardType="numbers-and-punctuation"
      returnKeyType="done"
      defaultValue={value !== undefined ? String(value) : ''}
      action={info ? <SettingInfo text={info} inputAction /> : undefined}
      onEndEditing={(e) => {
        const n = parseFloat(e.nativeEvent.text.replace(',', '.'))
        if (!isNaN(n)) onChange(integer ? Math.round(n) : n)
      }}
    />
  )
}

type SettingRowProps = {
  path: OpticalMeasureSettingsKeys
  cfg: SettingInputConfig
  value: unknown
  set: (path: OpticalMeasureSettingsKeys, value: unknown) => void
}

type FrameFreezeSectionProps = {
  open: boolean
  onToggle: () => void
  freezeStatus: OpticalMeasureFreezeStatus | undefined
  onFreezeCurrentFrame?: () => void
  onSetLatestFreeze?: () => void
  onClearFreeze?: () => void
}

const matchesPaths = (
  key: OpticalMeasureSettingsKeys,
  paths: readonly string[]
) => paths.some((path) => key === path || key.startsWith(`${path}.`))

function SettingRow({ path, cfg, value, set }: SettingRowProps) {
  if (cfg.type === 'bool') {
    const boolValue = value != null ? !!value : cfg.fallback
    return (
      <SettingCheckbox
        label={cfg.label}
        info={cfg.info}
        value={boolValue}
        onChange={() => set(path, !boolValue)}
      />
    )
  }
  if (cfg.type === 'number') {
    return (
      <NumInput
        label={cfg.label}
        info={cfg.info}
        value={numberValue.parse(value)}
        integer={cfg.integer}
        onChange={(v) => set(path, v)}
      />
    )
  }
  return (
    <SettingSelect
      label={cfg.label}
      info={cfg.info}
      options={cfg.options}
      value={stringValue.parse(value)}
      onChange={(v) => set(path, v)}
    />
  )
}

function FrameFreezeSection({
  open,
  onToggle,
  freezeStatus,
  onFreezeCurrentFrame,
  onSetLatestFreeze,
  onClearFreeze,
}: FrameFreezeSectionProps) {
  return (
    <SettingsSection
      collapsible
      title="Frame Freeze"
      open={open}
      onToggle={onToggle}
    >
      <View className="gap-1">
        <Typography variant="XS" className="text-black/30">
          Status
        </Typography>
        <Typography variant="XS" className="text-black/70">
          {getFreezeStatusLabel(freezeStatus)}
        </Typography>
      </View>
      <View className="gap-2">
        <Button
          width="full"
          variant="outlined"
          size="sm"
          label="Freeze Current Frame"
          disabled={!onFreezeCurrentFrame}
          onPress={onFreezeCurrentFrame}
        />
        <Button
          width="full"
          variant="outlined"
          size="sm"
          label="Set Latest Freeze"
          disabled={!onSetLatestFreeze}
          onPress={onSetLatestFreeze}
        />
        <Button
          width="full"
          variant="outlined"
          size="sm"
          label="Clear Freeze"
          disabled={!onClearFreeze}
          onPress={onClearFreeze}
        />
      </View>
    </SettingsSection>
  )
}

export function SettingsModal({
  open,
  close,
  settings,
  paths,
  sections = [],
  isDev = false,
  isDebug = false,
  onSave,
  onFreezeCurrentFrame,
  onSetLatestFreeze,
  onClearFreeze,
}: Props) {
  const [draft, setDraft] = useState<OpticalMeasureSettings>(settings)
  const [isContentReady, setIsContentReady] = useState(false)
  const freezeStatus = useFreezeStatus()
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(
    () =>
      Object.fromEntries([
        [FRAME_FREEZE_SECTION_ID, false],
        ...SETTINGS_CONFIG.map((s) => [s.id, s.defaultOpen]),
      ])
  )
  const { height: keyboardHeight } = useReanimatedKeyboardAnimation()

  const hasChanges = JSON.stringify(draft) !== JSON.stringify(settings)

  const set = (path: OpticalMeasureSettingsKeys, value: unknown) =>
    setDraft((prev) => setSetting(prev, path, value))

  const toggleSection = (id: string) =>
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }))

  const keyboardHeightStyle = useAnimatedStyle(() => ({
    height: -keyboardHeight.value,
  }))

  useEffect(() => {
    if (!open) {
      setIsContentReady(false)
      return
    }

    // Defer the heavy settings content past the modal's open paint so the
    // open animation stays smooth; the double rAF spans that first frame.
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => setIsContentReady(true))
    })

    return () => cancelAnimationFrame(frame)
  }, [open])

  useEffect(() => {
    if (open) setDraft(settings)
  }, [open, settings])

  return (
    <ModalWide
      title="Settings"
      open={open}
      close={close}
      showClose
      hideBottomSpace
    >
      {isContentReady && (
        <View className="flex-1 gap-4 px-16 pb-8">
          {SETTINGS_CONFIG.map((section: SectionConfig) => {
            const rows = section.items.filter(
              (item) =>
                matchesPaths(item.key, paths) &&
                isSettingVisible(item.level, isDev, isDebug) &&
                (item.visibleWhen == null || item.visibleWhen(draft))
            )

            if (rows.length === 0) return null

            return (
              <SettingsSection
                key={section.id}
                title={section.title}
                collapsible
                open={openSections[section.id]}
                onToggle={() => toggleSection(section.id)}
              >
                {rows.map((item) => (
                  <SettingRow
                    key={item.key}
                    path={item.key}
                    cfg={item}
                    value={getSetting(draft, item.key)}
                    set={set}
                  />
                ))}
              </SettingsSection>
            )
          })}

          {sections.includes(FRAME_FREEZE_SECTION_ID) && (
            <FrameFreezeSection
              open={openSections[FRAME_FREEZE_SECTION_ID]}
              onToggle={() => toggleSection(FRAME_FREEZE_SECTION_ID)}
              freezeStatus={freezeStatus}
              onFreezeCurrentFrame={onFreezeCurrentFrame}
              onSetLatestFreeze={onSetLatestFreeze}
              onClearFreeze={onClearFreeze}
            />
          )}
          <Animated.View style={keyboardHeightStyle} />

          {hasChanges && (
            <>
              <View className="pointer-events-none p-3 opacity-0">
                <Button variant="filled" size="sm" label="Save Settings" />
              </View>
              <View className="absolute bottom-0 left-0 right-0 bg-white/90 p-3">
                <Button
                  variant="filled"
                  size="sm"
                  label="Save Settings"
                  onPress={() => onSave(draft)}
                />
              </View>
            </>
          )}
        </View>
      )}
    </ModalWide>
  )
}
